<template>
  <a-form class="flex-grow-1 d-flex flex-column overflow-y-hidden" :form="addDomainForm">
    <div class="a-header">
      <span class="a-header--title slide-down-enter-active">Add Domain</span>
    </div>
    <div class="d-flex justify-content-center align-items-center pt-35-px" v-if="isInitingData">
      <Spinner />
    </div>
    <div class="a-body p-20-px flex-grow-1 d-flex flex-column overflow-y-auto" v-show="!isInitingData">
      <transition name="slide-up">
        <div v-if="!isAvailableToAddSubscribe">
          <p class="mb-0">
            <b>
              You have no domains available for adding right now, please contact us for details
            </b>
          </p>
        </div>
      </transition>

      <transition name="slide-up">
        <div v-show="isAddDomainSuccess && isAvailableToAddSubscribe">
          <a-alert showIcon type="success">
            <template slot="message">
              The web domain has now been submitted for crawling and baselining.
            </template>
          </a-alert>
          <br />
          <template v-if="!!resultAddedSubscribedDomain.committedBatchBaselineJobId">
            <p>
              For <b>Committed Monitoring</b>, the URLs have been submitted into a batch job for baselining.
            </p>
            <p>
              You can view the status of the batch job <b>ID: {{ resultAddedSubscribedDomain.committedBatchBaselineJobId }}&nbsp;</b>
              <router-link :to="'/batch-jobs/detail/' + resultAddedSubscribedDomain.committedBatchBaselineJobId">
                <u>here</u>
              </router-link>
              <br />
              Once the batch job is complete, an email will be sent to <u>{{ resultAddedSubscribedDomain.currentUserName }}</u>.
            </p>
          </template>
          <template v-else>
            <p>
              For <b>Committed Monitoring</b>, you have not entered any URLs to monitor yet.
              You can add them manually at
              <router-link :to="'/domain/' + resultAddedSubscribedDomain.id + '/add-webpage'">
                this
              </router-link>
              page.
            </p>
          </template>
          <p>
            For <b>Variable Monitoring</b>,
            WebOrion will now crawl your website and automatically add the results of the crawl into a batch job for baselining.
            <br />
            This may take a few minutes to a few hours depending on the size of your website.
          </p>
          <p class="mb-0">
            You can view the status of the batch job <b>ID:{{ resultAddedSubscribedDomain.variableCrawlJobId }}&nbsp;</b>
              <router-link :to="'/batch-jobs/detail-crawl/' + resultAddedSubscribedDomain.variableCrawlJobId">
                <u>here</u>
              </router-link>
            <br />
            Once the process is completed, a second email will be sent to <u>{{ resultAddedSubscribedDomain.currentUserName }}</u>.
          </p>
        </div>
      </transition>

      <div class="slide-up-enter-active" v-show="!isAddDomainSuccess && isAvailableToAddSubscribe">
        <a-steps :current="currentStep">
          <a-step v-for="item in steps" :key="item.title">
            <template slot="title">
              <span>
                <small>{{ item.title }}</small>
              </span>
            </template>
          </a-step>
        </a-steps>
        <div class="steps-content">
          <a-row :gutter="24" v-show="currentStep === 0">
            <a-col :span="8">
              <a-form-item label="Select Webpage Domain">
                <a-select
                  v-decorator="[
                    'webpageDomain',
                  ]"
                  size="large"
                  @change="handleChangeDomain"
                >
                  <template v-for="resource in resourcesAvailableToSubscribe">
                    <a-select-option :key="resource.id" :value="resource.id">
                      {{ resource.name }}
                    </a-select-option>
                  </template>
                </a-select>
              </a-form-item>
            </a-col>
          </a-row>
          <p class="label-bigger">
            <template v-if="currentStep === 0">
              Domain Subscription Information
            </template>
            <template v-if="currentStep === 1">
              Configure up to
              {{ selectedWebpageDomain().committed_limit || 0 }}
              URLs for "Committed" Monitoring every
              {{ selectedWebpageDomain().polling_interval || 0 }}
              minutes.
            </template>
            <template v-if="currentStep === 2">
              WebOrion will automatically crawl and monitor up to
              {{ selectedWebpageDomain().variable_limit || 0 }}
              URLs for Variable Monitoring in batches of
              {{ selectedWebpageDomain().variable_rate || 0 }}
              URLs every
              {{ selectedWebpageDomain().polling_interval || 0 }}
              minutes.
            </template>
          </p>
          <div class="domain-info" v-if="currentStep !== 2">
            <template v-if="currentStep === 0">
              <p class="mb-1">
                <a-tooltip placement="top" :overlayStyle="{ 'max-width': 'max-content' }">
                  <template slot="title">
                    <span class="fs-10">
                      Upper limit of total URLs that can be added to the system
                    </span>
                  </template>
                  <span class="highlight-cyan">
                    <u>Domain Size</u>:
                  </span>
                </a-tooltip>
                Monitors up to
                <strong>
                  {{ (selectedWebpageDomain().variable_limit || 0) + (selectedWebpageDomain().committed_limit || 0)  }}
                </strong>
                allocated as follows:
              </p>
              <p class="mb-0">
                - Up to {{ selectedWebpageDomain().committed_limit || 0 }} URLs for "Committed" Monitoring every {{ selectedWebpageDomain().polling_interval || 0 }} minutes.
              </p>
              <p class="mb-0">
                - Up to {{ selectedWebpageDomain().variable_limit || 0 }} URLs for "Variable" Monitoring in batches of {{ selectedWebpageDomain().variable_rate || 0 }} URLs every {{ selectedWebpageDomain().polling_interval || 0 }} minutes
              </p>
            </template>
            <template v-if="currentStep === 1">
              <p class="mb-0">
                If you do not have the URLs now, please skip this step and add them later.
              </p>
            </template>
          </div>
          <a-row :gutter="24" v-show="currentStep === 0">
            <a-col :span="24">
              <a-form-item class="mb-0">
                <a-checkbox :checked="isOpenAdvOptions" @change="isOpenAdvOptions = !isOpenAdvOptions">
                  Advanced Options
                </a-checkbox>
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="24" v-show="isOpenAdvOptions && currentStep === 0">
            <a-col :span="8">
              <a-form-item>
                <template slot="label">
                  User Agent
                  <a-tooltip placement="top">
                    <template slot="title">
                      <span class="fs-10">
                        Selects the user agent or browser type to be used
                        <br />
                        We provide both desktop and mobile user agents
                      </span>
                    </template>
                    <a-icon type="info-circle" theme="filled" />
                  </a-tooltip>
                </template>
                <a-select
                  v-decorator="[
                    'userAgent',
                  ]"
                  size="large"
                >
                  <a-select-option v-for="userAgent in userAgents" :key="userAgent.id" :value="userAgent.id">
                    {{ userAgent.alias }}
                  </a-select-option>
                </a-select>
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="24" v-show="isOpenAdvOptions && currentStep === 0">
            <a-col :span="12">
              <a-form-item extra="separate different keywords by a comma (,)">
                <template slot="label">
                  Whitelist of keyword(s) to check in HTML page
                  <a-tooltip placement="top" :overlayStyle="{ 'max-width': 'max-content' }">
                    <template slot="title">
                      <span class="fs-10">
                        Specifies what keyword(s) that we should monitor on your webpage
                        <br />
                        Examples: company name, webpage title
                      </span>
                    </template>
                    <a-icon type="info-circle" theme="filled" />
                  </a-tooltip>
                </template>
                <a-textarea
                  v-decorator="[
                    'keywordsInclude',
                  ]"
                  class="row-6 mb-0"
                  :rows="6"
                />
              </a-form-item>
            </a-col>
            <a-col :span="12">
              <a-form-item extra="separate different keywords by a comma (,)">
                <template slot="label">
                  Blacklist of keyword(s) to check in HTML page
                  <a-tooltip placement="top" :overlayStyle="{ 'max-width': 'max-content' }">
                    <template slot="title">
                      <span class="fs-10">
                        Specifies what keyword(s) that should not appear on your webpage
                        <br />
                        Examples: mysql/php error
                      </span>
                    </template>
                    <a-icon type="info-circle" theme="filled" />
                  </a-tooltip>
                </template>
                <a-textarea
                  v-decorator="[
                    'keywordsExclude',
                  ]"
                  class="row-6 mb-0" :rows="6"
                />
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="24" v-show="currentStep === 1">
            <a-col :span="16">
              <a-form-item extra="separate different URL by a newline (Enter)">
                <template slot="label">
                  Enter your list of URLs in the box below
                  <a-tooltip placement="top" :overlayStyle="{ 'max-width': 'max-content' }">
                    <template slot="title">
                      <span class="fs-10">
                        The URLs of the webpages to be monitored using the "Committed" monitoring type. One URL per line.
                        <br />
                        Please enter the absolute URLs (full URLs) for your webpages.
                        <br />  
                        Examples:
                        <dl>
	                      	<dt>
                            <b>
                              Regular HTTP page:
                            </b>
                          </dt>
	                      	<dd>
                            - {{ selectedWebpageDomain().name }}/index.html
                          </dd>
	                      	<dt>
                            <b>
                              Secure HTTP page:
                            </b>
                          </dt>
	                      	<dd>
	                      		- http<b>s</b>://{{ selectedWebpageDomain().name }}/index.html
	                      	</dd>
	                      	<dt>
                            <b>
                              Page with non-default port (e.g. 8080):
                            </b>
                          </dt>
	                      	<dd>
	                      		- {{ selectedWebpageDomain().protocolDomain }}<b>:8080</b>/demo/demo.html
	                      	</dd>
	                      </dl>
                      </span>
                    </template>
                    <a-icon type="info-circle" theme="filled" />
                  </a-tooltip>
                </template>
                <a-textarea
                  v-decorator="[
                    'listOfURLs',
                    {
                      rules: [
                        { validator: validateCommittedUrls }
                      ]
                    }
                  ]"
                  class="row-6 mb-0" :rows="6"
                  :placeholder="placeholderListUrl()"
                />
              </a-form-item>
            </a-col>
          </a-row>
          <a-row :gutter="24" v-show="currentStep === 2">
            <a-col :span="12">
              <a-form-item extra="Initial URL must match the selected domain">
                <template slot="label">
                  URL to start crawl from
                </template>
                <a-input
                  v-decorator="[
                    'urlToStartCrawlFrom',
                    {
                      rules: [
                        { required: true, message: 'Please input URL to start crawl.' },
                        { validator: validateSeedUrl }
                      ]
                    }
                  ]"
                  autocomplete="off"
                  placeholder="eg. http://www.example.com/index.html"
                />
              </a-form-item>
            </a-col>
            <a-col :span="12">
              <a-form-item>
                <template slot="label">
                  Crawl Depth
                </template>
                <a-select
                  v-decorator="[
                    'crawlDepth',
                  ]"
                  size="large"
                >
                  <template v-for="depth in crawlDepths">
                    <a-select-option :key="depth.value" :value="depth.value">
                      {{ depth.label }}
                    </a-select-option>
                  </template>
                </a-select>
              </a-form-item>
            </a-col>
          </a-row>
        </div>
      </div>
    </div>
    <div class="a-footer" v-if="isAvailableToAddSubscribe && !isInitingData">
      <a-button
        v-if="currentStep === 1 && !isAddDomainSuccess"
        size="large"
        type="danger"
        class="slide-down-enter-active"
        @click="skipStep"
      >
        Skip
      </a-button>
      <a-button
        v-if="currentStep > 0 && !isAddDomainSuccess"
        size="large"
        class="slide-down-enter-active ml-3"
        @click="prevStep"
      >
        Back
      </a-button>
      <a-button
        v-if="currentStep < steps.length - 1 && !isAddDomainSuccess"
        size="large"
        type="primary"
        class="slide-down-enter-active ml-3"
        :disabled="currentStep === 1 && !!addDomainForm.getFieldError('listOfURLs')"
        @click="nextStep"
      >
        Next
      </a-button>
      <a-button
        v-if="currentStep == steps.length - 1 && !isAddDomainSuccess"
        size="large"
        type="primary"
        class="slide-down-enter-active ml-3"
        @click="onSubmit"
      >
        Submit
      </a-button>
      <a-button
        v-if="isAddDomainSuccess"
        key="redirect"
        size="large"
        type="primary"
        class="slide-down-enter-active">
          <router-link :to="'/batch-jobs/list'">
          Complete
          </router-link>
      </a-button>
    </div>
  </a-form>
</template>

<script>
  import { REGEX_URL_WITH_PROTOCOL } from '@constants/regex';
  import DataService from '@shared/services/data.service';
  import DomainService from '../services/Domain.service';
  import { CrawlDepths } from '../consts/crawlDepths';
  import { DEFAULT_USER_AGENT_ID } from '../../Webpages/consts/Webpage.consts';


  const steps = [
    {
      title: 'Configure Webpage Domain',
    },
    {
      title: 'Configure URLs for Committed Monitoring',
    },
    {
      title: 'Configure URLs for Variable Monitoring',
    },
  ];

  export default {
    name: 'DomainMonitorAdd',
    data() {
      return {
        steps,
        currentStep: 0,
        resourcesAvailableToSubscribe: [],
        crawlDepths: [],
        userAgents: [],
        isInitingData: true,
        isAvailableToAddSubscribe: true,
        isOpenAdvOptions: false,
        isAddDomainSuccess: false,
        resultAddedSubscribedDomain: {
          id: '',
          currentUserName: '',
          committedBatchBaselineJobId: '',
          variableCrawlJobId: '',
        },
      };
    },
    beforeCreate() {
      this.addDomainForm = this.$form.createForm(this, {
        name: 'user-detail-form',
        onFieldsChange: (_, values) => {},
      });
    },
    beforeMount() {
      this.crawlDepths = CrawlDepths();
    },
    mounted() {
      this.getMasterData();
    },
    methods: {
      async getMasterData() {
        try {
          const [
            userAgentsData,
            resourcesAvailableToSubscribe,
          ] = await Promise.all([
            DataService.getUserAgents(),
            DomainService.fetchResourcesInactive(),
          ]);
          this.userAgents = userAgentsData || [];
          this.resourcesAvailableToSubscribe = (resourcesAvailableToSubscribe || []).map(item => {
            const protocolDomain = /^https?\:\/\//i.test(item.allowed_domain || '') ? item.allowed_domain : `http://${item.allowed_domain}`;
            return {...item, protocolDomain};
          });
          this.isAvailableToAddSubscribe = !!resourcesAvailableToSubscribe && !!resourcesAvailableToSubscribe.length;

          const defaultWebpageDomain = (resourcesAvailableToSubscribe && resourcesAvailableToSubscribe.length) ? resourcesAvailableToSubscribe[0] : {};
          this.addDomainForm.setFieldsValue({
            webpageDomain: defaultWebpageDomain.id,
            userAgent: DEFAULT_USER_AGENT_ID,
            urlToStartCrawlFrom: `https://${defaultWebpageDomain.allowed_domain}/`,
            crawlDepth: 5,
          });
        } catch (error) {
          this.$showMultiErrorMessage(error && error.messages || []);
        } finally {
          this.isInitingData = false;
        }
      },
      nextStep() {
        this.currentStep++;
      },
      skipStep() {
        this.currentStep++;
        this.addDomainForm.setFieldsValue({
          listOfURLs: '',
        });
      },
      prevStep() {
        this.currentStep--;
      },
      onChangeStep(currentStep) {
        this.currentStep = currentStep;
      },
      validateCommittedUrls(rule, value, callback) {
        const trimValue = (value || '').trim();
        if (!!trimValue) {
          const urls = trimValue.split('\n').filter(url => !!url);
          const isValid = urls.every(item => {
            return item.startsWith(this.selectedWebpageDomain().name)
              || item.startsWith(`http://${this.selectedWebpageDomain().name}`)
              || item.startsWith(`https://${this.selectedWebpageDomain().name}`);
          });
          if (isValid) {
            callback();
          } else {
            callback(`Please enter only URLs from ${this.selectedWebpageDomain().name}. Eg: https://${this.selectedWebpageDomain().name}/index.html`);
          }
        } else {
          callback();
        }
      },
      validateSeedUrl(rule, value, callback) {
        const trimValue = (value || '').trim();
        if (!!trimValue) {
          const isValid = trimValue.startsWith(this.selectedWebpageDomain().name)
              || trimValue.startsWith(`http://${this.selectedWebpageDomain().name}`)
              || trimValue.startsWith(`https://${this.selectedWebpageDomain().name}`);
          if (isValid) {
            callback();
          } else {
            callback(`Please enter only URLs from ${this.selectedWebpageDomain().name}. Eg: https://${this.selectedWebpageDomain().name}/index.html`);
          }
        } else {
          callback();
        }
      },
      onSubmit(e) {
        e.preventDefault();
        this.addDomainForm.validateFieldsAndScroll(async (err, valuesForm) => {
          if (!err) {
            const {
              webpageDomain,
              userAgent,
              keywordsInclude,
              keywordsExclude,
              listOfURLs,
              urlToStartCrawlFrom,
              crawlDepth,
            } = valuesForm;

            const committedUrls = (listOfURLs || '')
              .split('\n')
              .filter(item => !!item)
              .map(url => /^https?\:\/\//i.test(url) ? url : `http://${url}`);
            const seedUrl = /^https?\:\/\//i.test(urlToStartCrawlFrom) ? urlToStartCrawlFrom : `http://${urlToStartCrawlFrom}`;

            const bodyAddDomain = {
              domain_subscription_id: webpageDomain,
              user_agent_id: userAgent || DEFAULT_USER_AGENT_ID,
              text_string_appear: this.isOpenAdvOptions ? (keywordsInclude || '') : '',
              text_string_not_appear: this.isOpenAdvOptions ? (keywordsExclude || '') : '',
              committed_urls: committedUrls,
              seed_url: seedUrl,
              depth: crawlDepth || 5,
            }
            this.addSubscribedDomain(bodyAddDomain);
          }
        });
      },
      async addSubscribedDomain(bodyAddDomain) {
        try {
          const { id, committed_batch_baseline_job_id, variable_crawl_job_id } = await DomainService.addSubscribedDomain(bodyAddDomain);
          const currentUserName = (this.$store.state && this.$store.state.user && this.$store.state.user.data && this.$store.state.user.data.username) || '';
          this.resultAddedSubscribedDomain = {
            id,
            currentUserName,
            committedBatchBaselineJobId: committed_batch_baseline_job_id,
            variableCrawlJobId: variable_crawl_job_id,
          };
          this.isAddDomainSuccess = true;
        } catch (error) {
          this.$showMultiErrorMessage(error.messages || []);
        }
      },
      onComplete() {
        this.$showInfoMessage('Feature is coming!');
      },
      handleChangeDomain(idDomainSelect) {
        const domain = this.resourcesAvailableToSubscribe.find(item => item.id === idDomainSelect);
        this.addDomainForm.setFieldsValue({
          urlToStartCrawlFrom: domain ? `https://${domain.allowed_domain}/` : '',
        });
      },
      selectedWebpageDomain() {
        const id = this.addDomainForm && this.addDomainForm.getFieldValue('webpageDomain');
        if (!id) {
          return (this.resourcesAvailableToSubscribe || [])[0] || {};
        }
        const itemSelected = (this.resourcesAvailableToSubscribe || []).find(item => item.id === id);
        return itemSelected || {};
      },
      placeholderListUrl() {
        return `eg. http://${this.selectedWebpageDomain().name }/index.html (for regular HTTP)
        https://${this.selectedWebpageDomain().name}/index.html (for secure HTTP)
        http://${this.selectedWebpageDomain().name}:8080/demo/demo.html (for pages running on a non-default port, e.g. 8080)
        (one URL per line)`
      },
    },
  };
</script>

<style lang="scss" scoped>
  .steps-content {
    padding-top: 15px;
  }
  .label-bigger {
    color: #323232;
    font-size: 16px;
    font-weight: 500;
  }
  .domain-info {
    color: #323232;
    background-color: #F7F7F7;
    font-size: 13px;
    border-radius: 4px;
    padding: 20px;
  }
  .highlight-cyan {
    color: #20A4B8;
  }
</style>
